import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, X } from 'lucide-react';
import type { Person, Allocation } from '../../lib/peopleApi';
import type { Department } from '../../lib/departmentsApi';
import { useSetPersonDepartment } from '../../hooks/usePeople';
import { useUpdatePersonProjectAllocation } from '../../hooks/useKbProject';
import { useI18n } from '../../contexts/I18nContext';
import { Select } from '../ui/Select';
import { UtilizationBar, initials } from './UtilizationBar';

interface PeopleListTableProps {
  people: Person[];
  departments: Department[];
  /** When false the table is read-only (view mode). */
  editable: boolean;
}

/**
 * CLE-444: the /people list, rendered as a table. In edit mode each row exposes
 * inline controls to change the person's department (PATCH /people/:id/department)
 * and the weekly hours of each of their project allocations (markdown-first
 * upsert of the `## Echipa` row via addKbProjectMember). Utilization is derived
 * from the person's total allocated hours vs. capacity, so edits reflect live.
 */
export function PeopleListTable({ people, departments, editable }: PeopleListTableProps) {
  const { t } = useI18n();

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-600">
              {t('people.col.name')}
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-600">
              {t('people.col.department')}
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-600">
              {t('people.col.allocations')}
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-600">
              {t('people.col.utilization')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {people.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              departments={departments}
              editable={editable}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersonRow({
  person,
  departments,
  editable,
}: {
  person: Person;
  departments: Department[];
  editable: boolean;
}) {
  const { t } = useI18n();
  const setDepartment = useSetPersonDepartment();

  // Only current allocations (no end date or an end date in the future).
  const now = Date.now();
  const currentAllocations = person.allocations.filter(
    (a) => !a.endDate || new Date(a.endDate).getTime() >= now,
  );

  const departmentOptions = departments.map((d) => ({
    value: d.id,
    label: d.name,
    color: d.color,
  }));

  return (
    <tr className="align-top">
      {/* Name + avatar (always a link to the detail page). */}
      <td className="px-4 py-3">
        <Link to={`/people/${person.id}`} className="flex items-center gap-3 hover:underline">
          {person.avatarUrl ? (
            <img
              src={person.avatarUrl}
              alt={person.fullName}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
              {initials(person.fullName)}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate font-medium text-gray-900">{person.fullName}</div>
            <div className="truncate text-xs text-gray-500">{person.title ?? '—'}</div>
          </div>
        </Link>
      </td>

      {/* Department: editable Select in edit mode, chip otherwise. */}
      <td className="px-4 py-3">
        {editable ? (
          <Select
            label={t('people.col.department')}
            hideLabel
            clearable
            size="sm"
            value={person.departmentId ?? ''}
            onChange={(value) =>
              setDepartment.mutate({ id: person.id, departmentId: value || null })
            }
            placeholder={t('people.noDepartment')}
            options={departmentOptions}
            disabled={setDepartment.isPending}
            className="w-44"
          />
        ) : person.department ? (
          <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {person.department}
          </span>
        ) : (
          <span className="text-xs text-gray-400">{t('people.noDepartment')}</span>
        )}
      </td>

      {/* Allocations: one editable hours field per current project. */}
      <td className="px-4 py-3">
        {currentAllocations.length === 0 ? (
          <span className="text-xs text-gray-400">{t('people.noAllocations')}</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {currentAllocations.map((alloc) =>
              editable ? (
                <AllocationHoursEditor key={alloc.id} personId={person.id} allocation={alloc} />
              ) : (
                <div key={alloc.id} className="text-xs text-gray-700">
                  <span className="text-gray-500">{alloc.project.name}</span>{' '}
                  <span className="font-medium">
                    {alloc.hours}
                    {t('people.hoursPerWeekSuffix')}
                  </span>
                </div>
              ),
            )}
          </div>
        )}
      </td>

      {/* Utilization derived from total allocated hours vs. capacity. */}
      <td className="px-4 py-3">
        <div className="w-40">
          <UtilizationBar percent={person.utilizationPercent} />
        </div>
      </td>
    </tr>
  );
}

/**
 * Inline editor for a single allocation's weekly hours. Local draft state with a
 * save/cancel affordance; commits via the markdown-first allocation upsert
 * (personId + projectId + hours), which re-derives the DB rows and refreshes the
 * people/capacity caches.
 */
function AllocationHoursEditor({
  personId,
  allocation,
}: {
  personId: string;
  allocation: Allocation;
}) {
  const { t } = useI18n();
  const update = useUpdatePersonProjectAllocation();
  const [draft, setDraft] = useState(String(allocation.hours));
  const [error, setError] = useState<string | null>(null);

  const dirty = draft.trim() !== String(allocation.hours);

  function reset() {
    setDraft(String(allocation.hours));
    setError(null);
  }

  function save() {
    const n = Number(draft);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 168) {
      setError(t('people.edit.hoursRange'));
      return;
    }
    setError(null);
    update.mutate(
      {
        projectCodeOrId: allocation.projectId,
        input: { personId, allocationHours: n },
      },
      {
        onError: () => setError(t('people.edit.saveFailed')),
      },
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="w-32 truncate text-xs text-gray-500" title={allocation.project.name}>
        {allocation.project.name}
      </span>
      <input
        type="number"
        min={0}
        max={168}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') reset();
        }}
        className="w-16 rounded border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
        aria-label={allocation.project.name}
      />
      <span className="text-xs text-gray-400">{t('people.hoursPerWeekSuffix')}</span>
      {dirty && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={save}
            disabled={update.isPending}
            className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
            aria-label="save"
          >
            {update.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Check size={14} />
            )}
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={update.isPending}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
            aria-label="cancel"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
